import { ProviderSetupForm } from './setup-form'
import { useOrganizationName } from './shared'
import { ProviderStatusCard } from './status-card'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorNotice } from '@/modules/epilot-idp/shared'
import { useProviderSetup } from '@/services/phone-integration.service'

/**
 * One organization's live provider setup and the form that runs it. `withStatus` adds the status
 * card above the form; `onSetUp` runs after a successful setup run.
 */
export const OrganizationSetup = ({
  orgId,
  withStatus = false,
  onSetUp
}: {
  orgId: string
  withStatus?: boolean
  onSetUp?: () => void
}) => {
  const setup = useProviderSetup(orgId)
  const organizationName = useOrganizationName(orgId)

  if (setup.isPending) return <Skeleton className="h-48 w-full" />
  if (setup.error)
    return (
      <div className="space-y-2">
        <ErrorNotice
          message={`Could not read the phone setup. ${setup.error.message}`}
        />
        <Button onClick={() => void setup.refetch()} variant="outline">
          Try again
        </Button>
      </div>
    )

  return (
    <div className="space-y-6">
      {withStatus && <ProviderStatusCard setup={setup.data} />}
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">
          {setup.data.tenant ? 'Run the setup again' : 'Set up the phone'}
        </h2>
        <p className="text-sm text-muted-foreground">
          {setup.data.tenant
            ? 'Re-running keeps the tenant and SCIM configuration and replaces the SSO configuration, for example after a client secret rotation.'
            : 'Creates the organization’s Babelforce sub-account, turns on SCIM user provisioning with role management, and points its single sign-on at the Epilot IdP application.'}
        </p>
        {/* keyed by organization: a switch must never carry a typed secret over to another organization */}
        <ProviderSetupForm
          key={orgId}
          onSetUp={onSetUp}
          orgId={orgId}
          setup={setup.data}
          suggestedAccountName={organizationName}
        />
      </section>
    </div>
  )
}
